import { ArrowLeft, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { GroupForm } from "@/components/groups/group-form";
import { useAuth } from "@/hooks/use-auth";
import { useDeleteGroup, useGroup, useUpdateGroup } from "@/hooks/use-groups";
import { t } from "@/i18n/pl";

/** Group settings — the create form, prefilled, saving via PATCH. Driver only. */
export function EditGroupPage() {
  const { groupId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: group, isLoading } = useGroup(groupId);
  const updateGroup = useUpdateGroup(groupId);
  const deleteGroup = useDeleteGroup(t.editGroup.ended);
  const [endOpen, setEndOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }
  if (!group) return null;
  if (user?.id !== group.driver.id && !user?.is_admin) {
    return <Navigate to={`/grupy/${groupId}`} replace />;
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 mx-auto w-full max-w-lg duration-300">
      <Button asChild variant="ghost" size="sm" className="text-muted-foreground -ml-2 mb-3">
        <Link to={`/grupy/${groupId}`}>
          <ArrowLeft aria-hidden /> {t.common.back}
        </Link>
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{t.editGroup.title}</CardTitle>
          <CardDescription>{t.editGroup.subtitle}</CardDescription>
        </CardHeader>
        <CardContent>
          <GroupForm
            initial={group}
            pending={updateGroup.isPending}
            submitLabel={t.editGroup.submit}
            onSubmit={(input) =>
              updateGroup.mutate(input, {
                onSuccess: () => {
                  toast.success(t.editGroup.saved);
                  void navigate(`/grupy/${groupId}`);
                },
              })
            }
          />
        </CardContent>
      </Card>

      {/* The only way to remove a ride offer: the driver's own call, and the
          confirmation names how many passengers lose their seat. */}
      <Card className="border-destructive/40 mt-6">
        <CardHeader>
          <CardTitle>{t.editGroup.endTitle}</CardTitle>
          <CardDescription>{t.editGroup.endBody}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="destructive" onClick={() => setEndOpen(true)}>
            <Trash2 aria-hidden /> {t.editGroup.end}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={endOpen} onOpenChange={setEndOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.editGroup.endConfirmTitle}</DialogTitle>
            <DialogDescription>{t.editGroup.endConfirmBody(group.members.length)}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEndOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteGroup.isPending}
              onClick={() =>
                deleteGroup.mutate(groupId, {
                  onSuccess: () => {
                    setEndOpen(false);
                    void navigate("/");
                  },
                })
              }
            >
              {t.editGroup.end}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
